import { FunctionComponent, ReactElement, useCallback, useState } from 'react'
import styled from 'styled-components'
import {
	BodyFlow,
	CurrencyDisplay,
	H2Flow,
	H3Flow,
	useIntlConfig,
} from '@monthio/react-components'
import { useTranslation } from 'react-i18next'
import { TableFooter } from './TableFooter'
import { TransactionsTable } from './TransactionsTable'
import { useBudgetModule } from '../../../useBudgetModule'
import {
	BudgetChains,
	ConsumerEstimatesV2ResponseDto,
	Transaction,
} from '../../../types/budgetApi.types'
import { ExpenseEstimateDto } from '../../../types/expense.types'
import { ExpenseEstimate } from '../../Expenses/ExpenseEstimate'
import { budgetApiClient } from '../../../../../api/budgetApiClient'

const Wrapper = styled.div`
	height: 70vh;
	display: flex;
	flex-direction: column;
	gap: 24px;
`

const StyledH2Flow = styled(H2Flow)`
	margin: 2px 0;
`

const TotalMonthlyAmountContainer = styled.div`
	display: flex;
	flex-direction: row;
	justify-content: space-between;
	align-items: center;
`

const getTransactionsSortedByLatestDate = (
	transactions: Transaction[] | undefined,
): Transaction[] | undefined => {
	return transactions?.length
		? [...transactions].sort((a, b) => {
				const timeA = Date.parse(a.transactionDate)
				const timeB = Date.parse(b.transactionDate)

				if (isNaN(timeA) && isNaN(timeB)) return 0
				if (isNaN(timeA)) return 1
				if (isNaN(timeB)) return -1

				return timeB - timeA
			})
		: undefined
}

export const BudgetChainsTable: FunctionComponent<{
	itemKey: keyof BudgetChains
	budgetChains: ExpenseEstimateDto[]
	estimate: number
}> = ({ itemKey, budgetChains, estimate }) => {
	const { t } = useTranslation()
	const intlConfig = useIntlConfig({ showCurrency: true })
	const { data: budgetData } = useBudgetModule()

	const isCorrectedByUser =
		budgetData.userCorrected[itemKey as keyof ConsumerEstimatesV2ResponseDto] !=
		budgetData.estimates[itemKey as keyof ConsumerEstimatesV2ResponseDto]

	return (
		<Wrapper>
			{isCorrectedByUser ? (
				<BodyFlow>
					{t('budgetEstimates-chainsTable-userCorrected-subtitle', {
						expense: t(`${itemKey}-header`).toLocaleLowerCase(),
					})}{' '}
					<CurrencyDisplay
						amount={estimate}
						currencyFormatOptions={{
							allowNegativeValue: false,
							intlConfig,
						}}
					/>
				</BodyFlow>
			) : (
				<>
					<BodyFlow>{t(`${itemKey}-tooltip`)}</BodyFlow>

					<TotalMonthlyAmountContainer>
						<H3Flow>{`${t('assetExpenses-budgetModal-monthlyTotal-label')} ${t(
							`${itemKey}-header`,
						).toLocaleLowerCase()}`}</H3Flow>

						<CurrencyDisplay
							amount={estimate}
							currencyFormatOptions={{
								allowNegativeValue: false,
								intlConfig,
							}}
						/>
					</TotalMonthlyAmountContainer>
				</>
			)}

			<div>
				<StyledH2Flow>
					{t('budgetCategorizedTransactionsTable-text')}
				</StyledH2Flow>
				<BodyFlow>
					{isCorrectedByUser
						? t('budgetEstimates-chainsTable-userCorrected-description')
						: t('budgetEstimates-chainsTable-description')}
				</BodyFlow>
			</div>

			<div>
				{budgetChains.map((expenseEstimate, index) => (
					<ExpenseEstimate
						expenseEstimate={expenseEstimate}
						key={`EXPENSE_ESTIMATE::${index}`}
					/>
				))}
			</div>
		</Wrapper>
	)
}

export const BudgetCategorizedTransactionsTable = ({
	itemKey,
	omitTransactionsIfAny,
	estimate,
}: {
	itemKey: string
	omitTransactionsIfAny: boolean
	estimate: number
}): ReactElement => {
	const { t } = useTranslation()

	const [selectedTransaction, setSelectedTransaction] = useState<
		Transaction | undefined
	>()

	const reportTransaction = useCallback(async () => {
		if (selectedTransaction) {
			await budgetApiClient.createIncorrectTransaction(selectedTransaction)
			selectedTransaction.isReported = true
			setSelectedTransaction(undefined)
		}
	}, [selectedTransaction])

	const handleRowClick = useCallback((transaction: Transaction) => {
		setSelectedTransaction((prev) =>
			prev?.transactionId === transaction.transactionId
				? undefined
				: transaction,
		)
	}, [])

	const { data: budgetData } = useBudgetModule()
	const budgetChains = budgetData.budgetChains?.[itemKey as keyof BudgetChains]
	let budgetCategory =
		budgetData.budgetCategorizedTransactions?.budgetCategories.find(
			(x) => x.key == itemKey,
		)

	if (budgetChains != undefined) {
		return (
			<BudgetChainsTable
				itemKey={itemKey as keyof BudgetChains}
				budgetChains={budgetChains}
				estimate={estimate}
			/>
		)
	}

	if (budgetCategory == null) return <></>

	if (omitTransactionsIfAny) {
		budgetCategory = {
			...budgetCategory,
			transactions: [],
		}
	}

	const orderedTransactions = getTransactionsSortedByLatestDate(
		budgetCategory.transactions,
	)

	return (
		<Wrapper>
			<BodyFlow>{t(`${budgetCategory.key}-tooltip`)}</BodyFlow>
			<div>
				<StyledH2Flow>
					{t('budgetCategorizedTransactionsTable-text')}
				</StyledH2Flow>
				<BodyFlow>
					{orderedTransactions?.length
						? t('budgetCategorizedTransactionsTable-description')
						: t(
								'budgetCategorizedTransactionsTable-noTransactions-description',
							)}
				</BodyFlow>
			</div>
			<TransactionsTable
				transactions={orderedTransactions}
				selectedTransactionId={selectedTransaction?.transactionId}
				columnWidths={['25%', '35%', '20%', '20%']}
				onRowClick={handleRowClick}
			/>
			{selectedTransaction && (
				<TableFooter
					selectedTransaction={selectedTransaction}
					reportTransaction={reportTransaction}
				/>
			)}
		</Wrapper>
	)
}
